import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import cn from "classnames";
import LearnShell, { useLearnAccess } from "./LearnShell";
import CourseRow from "./CourseRow";
import PackageSheet from "./PackageSheet";
import { fetchCourse, toggleSave, FORMAT_LABEL, countLabel, formatDuration, progressPct, formatPrice, priceFor } from "./learnApi";

const SaveButton = ({ course, onToggle }) => (
  <button
    type="button"
    className={cn("learn-btn learn-btn--icon", { "learn-btn--saved": course.is_saved })}
    onClick={onToggle}
    aria-pressed={!!course.is_saved}
    aria-label={course.is_saved ? "Remove from my list" : "Save to my list"}
  >
    {course.is_saved ? (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
    )}
    <span>{course.is_saved ? "Saved" : "My list"}</span>
  </button>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const CourseBody = () => {
  const { slug } = useParams();
  const access = useLearnAccess();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [sheet, setSheet] = useState(null); // null | 'buy' | 'claim'

  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetchCourse(slug)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [slug, access.entitlements.length]);

  const onToggleSave = useCallback(async () => {
    if (!data?.course) return;
    const prev = data.course.is_saved;
    setData((d) => ({ ...d, course: { ...d.course, is_saved: !prev } }));
    try { await toggleSave(data.course.id); }
    catch (_) { setData((d) => ({ ...d, course: { ...d.course, is_saved: prev } })); }
  }, [data]);

  useEffect(() => {
    if (!trailerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setTrailerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [trailerOpen]);

  if (error) {
    return (
      <div className="learn-container">
        <div className="learn-empty learn-empty--dark">
          <h3>{error === "not_found" ? "We couldn't find that course." : "Something went wrong."}</h3>
          <Link to="/learn" className="learn-btn learn-btn--primary">Back to the hub</Link>
        </div>
      </div>
    );
  }

  const course = data?.course;
  const lessons = data?.lessons || [];
  const related = data?.related || [];
  const packages = data?.packages || [];
  const pct = course ? progressPct(course) : 0;
  const isGuide = course?.format === "guide";
  const isPackage = course?.access === "package";
  const locked = Boolean(course?.locked);
  const noLessons = lessons.length === 0;
  const isAttendee = Boolean(access.isAttendee);
  const cheapest = packages.slice().sort((a, b) => priceFor(a, isAttendee) - priceFor(b, isAttendee))[0];
  const resumePosition = (() => {
    if (!lessons.length) return 1;
    const firstIncomplete = lessons.find((l) => !l.completed);
    if (course?.last_position && pct > 0 && pct < 100) return course.last_position;
    return firstIncomplete ? firstIncomplete.position : 1;
  })();
  const primaryLabel = pct >= 100 ? "Watch again" : pct > 0 ? `Continue · Lesson ${resumePosition}` : isPackage ? "Start watching" : "Start course";
  const hero = course?.hero_image_url || course?.cover_image_url;
  const entitlement = isPackage
    ? access.entitlements.find((e) => (course.package_slugs || []).includes(e.packageSlug))
    : null;

  return (
    <>
      {course && (
        <>
          <section className={cn("learn-landing", { "learn-landing--guide": isGuide })}>
            {hero && <div className="learn-landing__bg" style={{ backgroundImage: `url(${hero})` }} aria-hidden="true" />}
            <div className="learn-landing__inner">
              <Link to="/learn" className="learn-back learn-back--light">← Postaway Academy</Link>
              <div className="learn-eyebrow learn-eyebrow--light">
                {FORMAT_LABEL[course.format]}
                {course.topic ? ` · ${course.topic}` : ""}
                {course.level ? ` · ${course.level}` : ""}
              </div>
              <h1 className="learn-landing__title">{course.title}</h1>
              <span className="learn-rule" aria-hidden="true" />
              {course.subtitle && <p className="learn-landing__subtitle">{course.subtitle}</p>}
              {course.description && <p className="learn-landing__desc">{course.description}</p>}
              <div className="learn-landing__meta">
                {countLabel(course)}
                {course.instructor_name ? ` · With ${course.instructor_name}` : ""}
              </div>
              <div className="learn-landing__actions">
                {locked ? (
                  <>
                    <button type="button" className="learn-btn learn-btn--primary" onClick={() => setSheet("buy")}>
                      <LockIcon /> Unlock{cheapest ? ` · from ${formatPrice(priceFor(cheapest, isAttendee))}` : ""}{isAttendee ? " (attendee rate)" : ""}
                    </button>
                    <button type="button" className="learn-btn learn-btn--ghost" onClick={() => setSheet("claim")}>
                      I'm a 2026 speaker — claim free access
                    </button>
                  </>
                ) : isGuide ? (
                  course.guide_url && (
                    <a href={course.guide_url} target="_blank" rel="noopener noreferrer" className="learn-btn learn-btn--primary">
                      Open guide (PDF)
                    </a>
                  )
                ) : (
                  lessons.length > 0 && (
                    <Link to={`/learn/${course.slug}/watch/${resumePosition}`} className="learn-btn learn-btn--primary">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true"><path d="M3 1.5v11l9-5.5z"/></svg>
                      {primaryLabel}
                    </Link>
                  )
                )}
                {course.trailer_video_url && (
                  <button type="button" className="learn-btn learn-btn--ghost" onClick={() => setTrailerOpen(true)}>Trailer</button>
                )}
                <SaveButton course={course} onToggle={onToggleSave} />
              </div>
              {isPackage && !locked && entitlement && (
                <div className="learn-landing__access">
                  <LockIcon />
                  {entitlement.source === "summit_2026_speaker"
                    ? "Unlocked — thank you for speaking in Breckenridge."
                    : entitlement.source === "summit_2026_attendee"
                      ? "Unlocked — you were there. Thanks for coming to Breckenridge."
                      : entitlement.source === "purchase"
                        ? "Unlocked with your package."
                        : "Unlocked."}
                </div>
              )}
              {pct > 0 && !isGuide && !locked && (
                <div className="learn-landing__progress">
                  <span className="learn-progress learn-progress--inline"><span className="learn-progress__bar" style={{ width: `${pct}%` }} /></span>
                  <span>{course.completed_count} of {course.lesson_count} complete</span>
                </div>
              )}
            </div>
          </section>

          <div className="learn-container learn-landing__body">
            <div className="learn-landing__main">
              {isGuide ? (
                <section className="learn-guide">
                  {course.cover_image_url && (
                    <a href={course.guide_url} target="_blank" rel="noopener noreferrer" className="learn-guide__cover">
                      <img src={course.cover_image_url} alt={`${course.title} cover`} />
                    </a>
                  )}
                  <div>
                    <h2 className="learn-section-title">About this guide</h2>
                    <p>{course.description}</p>
                    {course.guide_url && (
                      <a href={course.guide_url} target="_blank" rel="noopener noreferrer" className="learn-btn learn-btn--primary">Download PDF</a>
                    )}
                  </div>
                </section>
              ) : (
                <section>
                  <h2 className="learn-section-title">
                    {isPackage ? "Sessions" : course.format === "series" ? "Episodes" : course.format === "playlist" ? "Tracks" : "Lessons"}
                    <span className="learn-section-title__meta">{countLabel(course)}</span>
                  </h2>
                  {noLessons && isPackage ? (
                    <div className="learn-sessions-soon">
                      <div className="learn-eyebrow learn-eyebrow--light">Being edited now</div>
                      <h3>Sessions are on their way.</h3>
                      <p>
                        The summit was filmed start to finish. Each session is added here as soon as it's edited —
                        within a month of the event. {locked ? "Unlock now and you'll have every one the moment it lands." : "You'll have every one the moment it lands."}
                      </p>
                    </div>
                  ) : (
                    <ol className="learn-lessons">
                      {lessons.map((l) => {
                        const inner = (
                          <>
                            <div className="learn-lesson__thumb">
                              {l.poster_url ? <img src={l.poster_url} alt="" loading="lazy" /> : <div className="learn-card__media-fallback" />}
                              <span className="learn-lesson__duration">{l.locked ? <LockIcon /> : formatDuration(l.duration_seconds)}</span>
                            </div>
                            <div className="learn-lesson__body">
                              <div className="learn-lesson__title">
                                <span className="learn-lesson__num">{l.position}.</span> {l.title}
                                {l.completed && (
                                  <svg className="learn-lesson__check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-label="Completed"><circle cx="8" cy="8" r="7" fill="currentColor" opacity=".18"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                )}
                              </div>
                              {l.description && <p className="learn-lesson__desc">{l.description}</p>}
                            </div>
                          </>
                        );
                        return (
                          <li key={l.id}>
                            {l.locked ? (
                              <button type="button" className="learn-lesson learn-lesson--locked" onClick={() => setSheet("buy")}>
                                {inner}
                              </button>
                            ) : (
                              <Link to={`/learn/${course.slug}/watch/${l.position}`} className={cn("learn-lesson", { "learn-lesson--done": l.completed })}>
                                {inner}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </section>
              )}
            </div>

            <aside className="learn-landing__side">
              {isPackage && packages.length > 0 && (
                <div className={cn("learn-panel", { "learn-panel--unlock": locked })}>
                  <div className="learn-eyebrow learn-eyebrow--light">{locked ? "Unlock this course" : "Included in"}</div>
                  <ul className="learn-panel__packages">
                    {packages.map((p) => (
                      <li key={p.slug}>
                        <span>{p.title.replace(/^Summit 2026\s*/, "")}</span>
                        <strong>
                          {isAttendee && p.attendee_price_cents != null ? (
                            <><s className="learn-price-was">{formatPrice(p.price_cents)}</s> {formatPrice(p.attendee_price_cents)}</>
                          ) : formatPrice(p.price_cents)}
                        </strong>
                      </li>
                    ))}
                  </ul>
                  {locked && (
                    <>
                      <button type="button" className="learn-btn learn-btn--primary learn-panel__btn" onClick={() => setSheet("buy")}>Choose a package</button>
                      <button type="button" className="learn-panel__link" onClick={() => setSheet("claim")}>2026 speaker? Claim free access</button>
                    </>
                  )}
                  <p className="learn-panel__text">
                    {isAttendee
                      ? "Your 2026 attendee rate is shown. Streams inside Postaway Academy; recordings can't be downloaded."
                      : "Attended the 2026 Summit? Check out with the email you registered with for the attendee rate ($100 per track, $175 for both). Recordings stream here and can't be downloaded."}
                  </p>
                </div>
              )}
              {course.instructor_name && (
                <div className="learn-panel">
                  <div className="learn-eyebrow learn-eyebrow--light">{isPackage ? "About the recordings" : "Meet your instructor"}</div>
                  <div className="learn-instructor">
                    {course.instructor_avatar_url && <img src={course.instructor_avatar_url} alt="" />}
                    <div>
                      <div className="learn-instructor__name">{course.instructor_name}</div>
                      {course.instructor_title && <div className="learn-instructor__title">{course.instructor_title}</div>}
                    </div>
                  </div>
                  {course.instructor_bio && <p className="learn-panel__text">{course.instructor_bio}</p>}
                </div>
              )}
              {!isGuide && course.guide_url && (
                <a href={course.guide_url} target="_blank" rel="noopener noreferrer" className="learn-panel learn-panel--link">
                  <div className="learn-eyebrow learn-eyebrow--light">Class guide</div>
                  <div className="learn-panel__row">
                    <span>Download the companion PDF</span>
                    <span aria-hidden="true">↗</span>
                  </div>
                </a>
              )}
              <div className="learn-panel">
                <div className="learn-eyebrow learn-eyebrow--light">At a glance</div>
                <dl className="learn-facts">
                  <div><dt>Format</dt><dd>{FORMAT_LABEL[course.format]}</dd></div>
                  {course.level && <div><dt>Level</dt><dd>{course.level}</dd></div>}
                  {course.topic && <div><dt>Topic</dt><dd>{course.topic}</dd></div>}
                  {!isGuide && !noLessons && <div><dt>Length</dt><dd>{formatDuration(course.total_seconds)}</dd></div>}
                  <div><dt>For</dt><dd>{course.audience === "all" ? "Hosts & creators" : course.audience === "host" ? "Hosts" : "Creators"}</dd></div>
                  {isPackage && <div><dt>Access</dt><dd>{locked ? "Package required" : "Unlocked"}</dd></div>}
                </dl>
              </div>
            </aside>
          </div>

          <div className="learn-container">
            <CourseRow title="More like this" courses={related} seeAllTo="/learn/library" packages={access.packages} isAttendee={isAttendee} />
          </div>

          {trailerOpen && course.trailer_video_url && (
            <div className="learn-modal" role="dialog" aria-modal="true" aria-label={`${course.title} trailer`} onClick={() => setTrailerOpen(false)}>
              <div className="learn-modal__inner" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="learn-modal__close" aria-label="Close trailer" onClick={() => setTrailerOpen(false)}>×</button>
                <video src={course.trailer_video_url} controls autoPlay playsInline poster={course.cover_image_url || undefined} />
              </div>
            </div>
          )}

          <PackageSheet
            open={sheet !== null}
            initialTab={sheet === "claim" ? "claim" : "buy"}
            onClose={() => setSheet(null)}
            packages={packages}
            isAttendee={isAttendee}
            preferredSlug={packages.find((p) => p.audience === course.audience)?.slug}
            returnTo={`/learn/${course.slug}`}
          />
        </>
      )}
    </>
  );
};

const LearnCourse = () => (
  <LearnShell dark redirectWhenComingSoon>
    <CourseBody />
  </LearnShell>
);

export default LearnCourse;
